/**
 * Authentication service for managing login, logout, and session state.
 *
 * This service manages auth state through Zustand (useAppStore) for authentication state.
 * The API client reads tokens from localStorage directly, so this service
 * only needs to manage the auth state and coordinate initialization.
 */

import { mixpanel } from "common/analytics";
import { queryClient } from "common/queryClient";
import { default as decode } from "jwt-decode";
import { useAppStore } from "stores/useAppStore";
import { useUIStore } from "stores/useUIStore";

export type JWT = {
  user: string;
  iat: number;
  exp: number;
  impersonated?: boolean;
};

export type TokenResponse = {
  token: string;
};

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT;

/**
 * Fetch a new JWT token using email and password.
 */
export async function fetchToken(
  email: string,
  password: string
): Promise<TokenResponse> {
  console.log("Fetching token from API endpoint:", API_ENDPOINT);
  const response = await fetch(`${API_ENDPOINT}/v1/tokens/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mail: email, password }),
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    throw error;
  }

  return (await response.json()) as TokenResponse;
}

/**
 * Perform login with email and password.
 * This fetches a token and stores it, but does NOT initialize the app.
 * The caller should handle app initialization separately.
 */
export async function login(
  email: string,
  password: string
): Promise<TokenResponse> {
  const { setLoggingIn, setAuthError } = useAppStore.getState();

  setLoggingIn(true);
  setAuthError(false);

  try {
    console.log("Attempting login (in login()) for:", email);
    const tokenResponse = await fetchToken(email, password);
    localStorage.setItem("jwt", tokenResponse.token);
    return tokenResponse;
  } catch (error) {
    setAuthError(true);
    throw error;
  } finally {
    // Keep loginInProgress true - caller will set false after initialization
  }
}

/**
 * Clear all auth state and redirect to login.
 * This resets all stores and clears caches.
 */
export function logout(): void {
  // Clear all storage
  localStorage.clear();

  // Clear React Query cache
  queryClient.clear();

  // Reset all Zustand stores
  useAppStore.getState().reset();
  useUIStore.getState().reset();

  // Re-enable tracking (in case this was an impersonated session) before reset
  mixpanel.opt_in_tracking();
  mixpanel.track("Logout successful");
  mixpanel.reset();

  // Hard redirect to login (preserve pathname for staging's /{gitsha}/index.html)
  window.location.href = window.location.pathname + "#/login";
}

/**
 * Set login complete state after successful initialization.
 */
export function setLoginComplete(): void {
  const { setAuthenticated, setLoggingIn } = useAppStore.getState();
  setAuthenticated(true);
  setLoggingIn(false);
}

/**
 * Set login failed state.
 */
export function setLoginFailed(): void {
  const { setLoggingIn, setAuthError } = useAppStore.getState();
  setLoggingIn(false);
  setAuthError(true);
}

/**
 * Decode the current JWT token and return user info.
 */
export function decodeToken(): JWT | null {
  const token = localStorage.getItem("jwt");
  if (!token) return null;

  try {
    return decode<JWT>(token);
  } catch {
    return null;
  }
}

export type PasswordResetResponse = {
  message: string;
  emailSent: boolean;
};

/**
 * Send a password reset email.
 */
export async function sendPasswordResetEmail(
  email: string
): Promise<PasswordResetResponse> {
  const response = await fetch(`${API_ENDPOINT}/v1/users/passwordReset`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mail: email }),
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    throw error;
  }

  return (await response.json()) as PasswordResetResponse;
}
